// care.js — CareTeam + CarePlan depth for the ONLINE-PRIMARY (main-2) ward.
//
// Reads and writes go browser-DIRECT to Medplum AS THE LOGGED-IN USER (the one
// RLS-scoped session in medplum-client.js) — no Express, no service account.
// Every function here is bounded by the caller's own AccessPolicy; a write the
// policy forbids fails loud (§2). What is honestly allowed was verified live on
// the local rig (localhost:8103) before this module was written — see the
// findings block at the bottom.
//
// WHAT IS HONESTLY WRITABLE (verified as a doctor on the opd-doctor-rowlevel policy):
//   • CarePlan  — WRITABLE in the doctor's department compartment (the policy arm
//     `CarePlan?_compartment=%department` is NOT readonly). A direct updateResource
//     on an in-compartment plan succeeds. So "change the plan" = a direct FHIR
//     write here, RLS-safe-by-construction (the bed's patient is in the ward's
//     department compartment). We do NOT route this through the care-plan-operation
//     Bot: that Bot is runAsUser=true, so it is bounded by THIS SAME doctor policy —
//     it adds a hop without adding authority, and on the rig its custom-op dispatch
//     returned Forbidden anyway.
//   • CareTeam  — READONLY for a doctor DIRECTLY (the policy arm carries
//     `readonly:true`), so add/remove goes through the PRIVILEGED care-team-operation
//     Bot (2026-07-10): it is runAsUser=FALSE, runs under its own
//     bot-care-team-operation policy, and enforces a SENIORITY-HIERARCHY guard off the
//     server-set caller identity — a caller may add/remove only members ranked strictly
//     BELOW them on the jr-resident<sr-resident<associate-prof<hod ladder (same-rank or
//     above → a clean refusal the UI surfaces). addCareTeamMember() below calls
//     Patient/$set-care-team as the logged-in user; the Bot decides. (This replaced the
//     old runAsUser=true model, where the Bot inherited the doctor's readonly ceiling
//     and every call returned Forbidden.)
//   • Goal      — READ is FORBIDDEN for a doctor (Goal is absent from the rowlevel
//     policy). So plan goals are surfaced from CarePlan.goal[].display only; we never
//     fetch Goal resources on the ward read path.
//   • The definitional (template) CareTeam for the ADMIN default-team config IS
//     writable — the admin runs on the wildcard opd-admin policy (verified).
//   • CareTeam participation status (invites, 2026-07-10): FHIR R4
//     CareTeam.participant has NO status element (only member/role/onBehalfOf/
//     period), and CareTeam is doctor-READONLY here (the Bot is the only write
//     path). So the invite lifecycle honestly lives on a `care-team-invite` FHIR
//     Task ASSIGNED TO THE TARGET (Task.owner = the invited Practitioner,
//     Task.for = the patient, Task.focus = the CareTeam): an OPEN invite Task =
//     'proposed', completed + businessStatus 'accepted' = active, completed +
//     businessStatus 'declined' = declined (shown honestly; a senior removes the
//     member via the Bot). We do NOT stamp period.start / an extension onto the
//     participant — that would need a CareTeam write the target is not allowed
//     (verified: direct CareTeam write as a doctor → Forbidden), and the Bot's
//     seniority guard refuses self-edits by design. Derived-from-Task is the
//     no-fake-writes shape. (Verified live: a doctor CAN create a Task owned by
//     another practitioner — the Task inherits the patient's department
//     compartment, so the `Task?_compartment=%department` arm admits the write —
//     and the target reads + completes it under `Task?owner=%profile`.)
import { getMedplum, deriveCurrentUser } from './medplum-client.js';
import { runAgentLoop } from './medplum-ai.js';
import { listProtocolsForDepartment } from './medplum-protocols.js';

// ── Identifier systems (MUST match server/opd/care-structures.mjs + the bots) ──
export const CAREPLAN_PATIENT_SYSTEM = 'https://healther.io/opd/careplan-patient'; // per-patient plan
export const CAREPLAN_EPISODE_SYSTEM = 'https://healther.io/opd/careplan-episode'; // per (patient,dept) episode plan
export const CARETEAM_PATIENT_SYSTEM = 'https://healther.io/opd/careteam-patient'; // per-patient team
export const CARETEAM_DEPT_SYSTEM    = 'https://healther.io/opd/careteam-dept';    // dept roster team
export const CARETEAM_DEFAULT_SYSTEM = 'https://healther.io/opd/careteam-default'; // dept DEFAULT (template) team
export const DEPT_SLUG_SYSTEM        = 'https://healther.io/opd/department-slug';
export const SENIORITY_SYSTEM        = 'https://healther.io/opd/seniority';

// ── CareTeam add-member availability (surfaced to the UI) ────────────────────
// LIVE (2026-07-10): the privileged care-team-operation Bot provides the write path
// (see the CareTeam note in the header). The affordance is enabled; the Bot enforces
// the seniority-hierarchy guard and returns an honest refusal message when the caller
// is not senior enough, which the UI shows verbatim. The seniority ladder, for the
// role picker (participant.role) — must match server/opd/staff-auth.mjs.
export const CARE_TEAM_ADD_AVAILABLE = true;
export const SENIORITY_ROLE_OPTIONS = [
  { code: 'hod', label: 'Head of Department' },
  { code: 'associate-prof', label: 'Associate Professor' },
  { code: 'sr-resident', label: 'Senior Resident' },
  { code: 'jr-resident', label: 'Junior Resident' },
];

// ── The seniority LADDER — the SAME single-source vocabulary the care-team Bot
// (bots/care-team-operation.js SENIORITY_RANK) and server/opd/staff-auth.mjs use.
// jr-resident(1) < sr-resident(2) < associate-prof(3) < hod(4); the short forms
// jr/sr are accepted (Spaces/agent surfaces emit them). Rank 0 = no seniority.
export const SENIORITY_RANK = {
  'jr-resident': 1, jr: 1,
  'sr-resident': 2, sr: 2,
  'associate-prof': 3,
  hod: 4,
};
const SENIORITY_LABELS = {
  'jr-resident': 'Junior Resident', jr: 'Junior Resident',
  'sr-resident': 'Senior Resident', sr: 'Senior Resident',
  'associate-prof': 'Associate Professor',
  hod: 'Head of Department',
};
export function seniorityRankOf(code) {
  return SENIORITY_RANK[String(code || '').trim().toLowerCase()] || 0;
}
export function seniorityLabelOf(code) {
  const c = String(code || '').trim().toLowerCase();
  return SENIORITY_LABELS[c] || '';
}

const idOf = (ref) => String((ref && ref.reference) || ref || '').split('/').pop() || '';
const refDisplay = (r) => (r && r.display) || ((r && r.reference) || '').replace(/^[^/]+\//, '') || '';

// ══════════════════════════════════════════════════════════════════════════════
// PURE shapers + progress — unit-testable, no I/O.
// ══════════════════════════════════════════════════════════════════════════════

// CarePlan.activity[].detail.status → one of the three timeline bands the UI shows
// (plus 'other' for cancelled/stopped so they never inflate progress). FHIR R4
// CarePlan activity statuses: not-started | scheduled | in-progress | on-hold |
// completed | cancelled | stopped | unknown | entered-in-error.
const ACTIVITY_BAND = {
  completed: 'done',
  'in-progress': 'current',
  'not-started': 'upcoming',
  scheduled: 'upcoming',
  'on-hold': 'upcoming',
  cancelled: 'other',
  stopped: 'other',
  'entered-in-error': 'other',
  unknown: 'upcoming',
};
export function activityBand(status) {
  return ACTIVITY_BAND[String(status || '').toLowerCase()] || 'upcoming';
}

/** Shape one CarePlan.activity into the row the timeline renders. PURE. */
export function shapeActivity(a, i) {
  const d = (a && a.detail) || {};
  const status = String(d.status || 'unknown').toLowerCase();
  return {
    index: i,
    status,
    band: activityBand(status),
    description: d.description || (d.code && (d.code.text || d.code.coding?.[0]?.display)) || 'Care step',
    scheduled: d.scheduledString
      || (d.scheduledPeriod && d.scheduledPeriod.start ? String(d.scheduledPeriod.start).slice(0, 10) : '')
      || (d.scheduledTiming && d.scheduledTiming.repeat && d.scheduledTiming.repeat.frequency ? 'recurring' : ''),
    category: (d.category && (d.category.text || d.category.coding?.[0]?.display)) || '',
  };
}

/** Shape a CarePlan into the DTO the Care-plan tab renders. PURE. */
export function shapeCarePlan(cp) {
  if (!cp) return null;
  const activities = (Array.isArray(cp.activity) ? cp.activity : []).map(shapeActivity);
  // BUG FIX (2026-07-19, "raw UUID goal chip" seen on the bed-workspace tour):
  // this used to fall back to refDisplay(g), which strips "Goal/" off the
  // reference and returns the bare Goal id when g.display is absent. That
  // fallback is safe for CareTeam member/subject refs elsewhere in this file
  // (a real name is resolvable, or the batched Practitioner read above fixes
  // it) but NOT for goals: Goal is read-forbidden under RLS (comment above,
  // "Goal is deliberately NOT fetched"), so a goal reference with no .display
  // can never be resolved to real text client-side — refDisplay(g) was
  // therefore rendering the naked UUID as if it were the goal's name. Fall
  // straight to the generic label instead of guessing from the reference.
  const goals = (Array.isArray(cp.goal) ? cp.goal : []).map((g) => ({
    ref: g.reference || null,
    display: g.display || 'Goal',
  }));
  return {
    id: cp.id,
    title: cp.title || cp.description || 'Care plan',
    status: cp.status || 'unknown',
    intent: cp.intent || 'plan',
    start: (cp.period && cp.period.start) || null,
    end: (cp.period && cp.period.end) || null,
    updatedAt: (cp.meta && cp.meta.lastUpdated) || null,
    activities,
    goals,
    isPatientPlan: (cp.identifier || []).some((i) => i.system === CAREPLAN_PATIENT_SYSTEM),
    raw: cp,
  };
}

/**
 * How far along a plan is, from its ACTIVITY statuses (owner spec: done / current /
 * upcoming). PURE + total. `pct` is completed of the count that can progress
 * (done+current+upcoming — 'other'/cancelled excluded so a cancelled step never
 * drags the bar). No timeline (no activities) → hasTimeline:false, pct:0.
 */
export function carePlanProgress(planOrActivities) {
  const acts = Array.isArray(planOrActivities)
    ? planOrActivities
    : (planOrActivities && Array.isArray(planOrActivities.activities))
      ? planOrActivities.activities
      : [];
  const bands = { done: 0, current: 0, upcoming: 0, other: 0 };
  for (const a of acts) bands[a.band || activityBand(a.status)] = (bands[a.band || activityBand(a.status)] || 0) + 1;
  const progressable = bands.done + bands.current + bands.upcoming;
  const pct = progressable ? Math.round((bands.done / progressable) * 100) : 0;
  return {
    total: acts.length,
    done: bands.done,
    current: bands.current,
    upcoming: bands.upcoming,
    other: bands.other,
    progressable,
    pct,
    hasTimeline: acts.length > 0,
  };
}

/** Shape a CareTeam into the DTO the Care-team tab renders. PURE. */
export function shapeCareTeam(ct) {
  if (!ct) return null;
  const members = (Array.isArray(ct.participant) ? ct.participant : []).map((p) => {
    const roles = Array.isArray(p.role) ? p.role : [];
    const roleText = roles
      .map((r) => r.text || (r.coding && (r.coding[0]?.display || r.coding[0]?.code)))
      .filter(Boolean)
      .join(' · ');
    // Seniority carried ON the participant role — a fallback hint when the member
    // has no readable PractitionerRole. Two honest carriers: a SENIORITY_SYSTEM
    // coding (care-structures / the Bot's jr|sr|associate-prof|hod codes) or an
    // exact ladder code as plain role text (the Bot free-texts codes outside its
    // short-form map, e.g. 'sr-resident' — see bots/care-team-operation.js
    // buildRole). Only exact ladder codes match; arbitrary text never ranks.
    let seniorityHint = '';
    const bump = (code) => {
      if (seniorityRankOf(code) > seniorityRankOf(seniorityHint)) seniorityHint = String(code).toLowerCase();
    };
    for (const r of roles) {
      for (const c of (r.coding || [])) {
        if (c.system === SENIORITY_SYSTEM && c.code) bump(c.code);
      }
      if (r.text && SENIORITY_RANK[String(r.text).trim().toLowerCase()]) bump(r.text);
    }
    const memberRef = (p.member && p.member.reference) || '';
    return {
      name: refDisplay(p.member) || 'Unnamed member',
      role: roleText || '',
      practitionerRef: /^Practitioner\//.test(memberRef) ? memberRef : null,
      memberRef: memberRef || null,
      memberType: memberRef ? memberRef.split('/')[0] : null,
      seniorityHint,
      onBehalfOf: refDisplay(p.onBehalfOf) || '',
    };
  });
  return {
    id: ct.id,
    name: ct.name || 'Care team',
    status: ct.status || 'active',
    subjectDisplay: refDisplay(ct.subject) || '',
    managingOrg: (Array.isArray(ct.managingOrganization) ? ct.managingOrganization : (ct.managingOrganization ? [ct.managingOrganization] : []))
      .map(refDisplay).filter(Boolean).join(', '),
    members,
    isPatientTeam: (ct.identifier || []).some((i) => i.system === CARETEAM_PATIENT_SYSTEM),
    raw: ct,
  };
}

// Newest-updated wins when a patient somehow has more than one plan/team.
function newest(list) {
  return (list || []).slice().sort((a, b) =>
    String(b?.meta?.lastUpdated || '').localeCompare(String(a?.meta?.lastUpdated || '')))[0] || null;
}

// ══════════════════════════════════════════════════════════════════════════════
// READ — the bed's Care plan + Care team, RLS-scoped to the logged-in user.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Load everything the two ward tabs need for one patient, in ONE call:
 *   { plan, plans, team, progress }
 * plan  = the patient's primary CarePlan (patient-scoped one preferred, else newest)
 * plans = every CarePlan on this patient the caller can see (shaped)
 * team  = the patient's CareTeam (patient-scoped by subject) shaped, or null
 * progress = carePlanProgress(plan)
 * Goal is deliberately NOT fetched (read-forbidden for doctors); goal names come
 * from CarePlan.goal[].display.
 */
export async function loadPatientCare(patientId) {
  if (!patientId) return { plan: null, plans: [], team: null, progress: carePlanProgress([]) };
  const { medplum } = await getMedplum();
  const ref = `Patient/${patientId}`;

  // Fail loud (§1.3): a failed CarePlan/CareTeam read must NOT masquerade as an
  // empty "no plan / no team yet" state — that empty state invites creating a
  // DUPLICATE plan/team (contract §2 "patient twice"). Let the rejection propagate
  // so the tab's own ErrorPane + Retry path (BedWorkspace CarePlanTab/CareTeamTab)
  // renders instead. The per-member enrichment reads (seniority / names / invites,
  // in the tab) stay best-effort — they degrade the display, not the record's
  // existence.
  const [rawPlans, rawTeams] = await Promise.all([
    medplum.searchResources('CarePlan', `subject=${ref}&_sort=-_lastUpdated&_count=50`),
    medplum.searchResources('CareTeam', `subject=${ref}&_sort=-_lastUpdated&_count=50`),
  ]);

  // Primary plan: the per-patient plan if present, else the newest — but a
  // revoked/entered-in-error plan never wins the primary pick (it stays in
  // `plans` for the record).
  const liveOnly = (rawPlans || []).filter((c) => c.status !== 'revoked' && c.status !== 'entered-in-error');
  const patientPlan = liveOnly.find((c) => (c.identifier || []).some((i) => i.system === CAREPLAN_PATIENT_SYSTEM));
  const primary = patientPlan || newest(liveOnly) || newest(rawPlans);
  const plan = shapeCarePlan(primary);
  const plans = (rawPlans || []).map(shapeCarePlan);

  // Primary team: per-patient team if present, else the newest subject-scoped team.
  const patientTeam = (rawTeams || []).find((c) => (c.identifier || []).some((i) => i.system === CARETEAM_PATIENT_SYSTEM));
  const team = shapeCareTeam(patientTeam || newest(rawTeams));

  return { plan, plans, team, progress: carePlanProgress(plan) };
}

// ══════════════════════════════════════════════════════════════════════════════
// CARE-TEAM HIERARCHY — seniority per member (PractitionerRole is the source of
// truth, the SAME ladder the Bot ranks on) + the PURE tree the tab renders.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Resolve each Practitioner member's seniority code from their ACTIVE
 * PractitionerRole (one batched search — PractitionerRole read is granted on every
 * staff policy). Returns { 'Practitioner/<id>': 'hod' | 'sr-resident' | … }.
 * Members without a seniority coding are simply absent. Best-effort: a search
 * failure returns {} so the tab still renders (with role-coding hints only).
 */
export async function loadTeamSeniorities(team) {
  const refs = [...new Set(((team && team.members) || []).map((m) => m.practitionerRef).filter(Boolean))];
  if (!refs.length) return {};
  const { medplum } = await getMedplum();
  let roles = [];
  try {
    roles = await medplum.searchResources('PractitionerRole',
      `practitioner=${encodeURIComponent(refs.join(','))}&active=true&_count=200`);
  } catch (_) { return {}; }
  const out = {};
  for (const r of (roles || [])) {
    const pref = r.practitioner && r.practitioner.reference;
    if (!pref) continue;
    for (const cc of (r.code || [])) {
      for (const c of (cc.coding || [])) {
        if (c.system === SENIORITY_SYSTEM && seniorityRankOf(c.code) > seniorityRankOf(out[pref])) {
          out[pref] = String(c.code).toLowerCase();
        }
      }
    }
  }
  return out;
}

/**
 * Resolve human names for linked members whose participant carries NO display —
 * the care-team Bot writes `member: { reference }` bare, so a freshly-added
 * member would otherwise render as a raw UUID. One batched Practitioner read
 * (granted on every staff policy) for just the display-less refs → { ref: name }.
 * Best-effort: a failure returns {} and those members show their id (honest).
 */
export async function loadTeamMemberNames(team) {
  const missing = ((team && team.members) || [])
    .filter((m) => m.practitionerRef && (!m.name || m.name === idOf(m.practitionerRef)))
    .map((m) => idOf(m.practitionerRef));
  const ids = [...new Set(missing)];
  if (!ids.length) return {};
  const { medplum } = await getMedplum();
  let pracs = [];
  try {
    pracs = await medplum.searchResources('Practitioner', `_id=${encodeURIComponent(ids.join(','))}&_count=${ids.length}`);
  } catch (_) { return {}; }
  const out = {};
  for (const p of (pracs || [])) {
    const n = (p.name && p.name[0]) || null;
    const name = (n && (n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' '))) || '';
    if (name) out[`Practitioner/${p.id}`] = name;
  }
  return out;
}

// The tree's rank tiers, top-down. 'staff' = linked Practitioners with NO
// seniority anywhere (rank 0) — still clinicians, so they sit at the bottom of
// the clinical tree rather than with patient/family.
const TREE_TIERS = [
  { code: 'hod', rank: 4 },
  { code: 'associate-prof', rank: 3 },
  { code: 'sr-resident', rank: 2 },
  { code: 'jr-resident', rank: 1 },
  { code: 'staff', rank: 0 },
];

/**
 * Build the hierarchy tree the Care-team tab renders. PURE.
 *   members       shapeCareTeam().members
 *   seniorityByRef  loadTeamSeniorities() result (may be {})
 *   inviteByRef   optional { 'Practitioner/<id>': { state, taskId } } from
 *                 listCareTeamInvites() — stamps participation on each node.
 * Returns { tiers: [{ code, rank, label, members:[…] }], others: […] } where
 * `tiers` holds LINKED Practitioners grouped by seniority (empty tiers dropped,
 * top rank first — depth in the tree = index in this array) and `others` holds
 * everyone else (patient/family/unlinked display-only members), grouped apart per
 * the owner spec. Each tree member gains { seniority, rankLabel, participation }.
 * participation: 'active' (default — pre-invite members), 'proposed' (open invite
 * Task), or 'declined'.
 */
export function buildCareTeamTree(members, seniorityByRef = {}, inviteByRef = {}) {
  const tiers = TREE_TIERS.map((t) => ({
    code: t.code,
    rank: t.rank,
    label: t.code === 'staff' ? 'Team member' : seniorityLabelOf(t.code),
    members: [],
  }));
  const others = [];
  for (const m of (members || [])) {
    if (!m.practitionerRef) { others.push(m); continue; }
    const code = seniorityByRef[m.practitionerRef] || m.seniorityHint || '';
    const rank = seniorityRankOf(code);
    const invite = inviteByRef[m.practitionerRef] || null;
    const participation = invite
      ? (invite.state === 'accepted' ? 'active' : invite.state) // 'proposed' | 'declined'
      : 'active';
    const node = {
      ...m,
      seniority: rank > 0 ? code : '',
      rankLabel: rank > 0 ? seniorityLabelOf(code) : '',
      participation,
      inviteTaskId: invite ? invite.taskId : null,
    };
    (tiers.find((t) => t.rank === rank) || tiers[tiers.length - 1]).members.push(node);
  }
  return { tiers: tiers.filter((t) => t.members.length > 0), others };
}

// ══════════════════════════════════════════════════════════════════════════════
// CARE-TEAM INVITES — the accept/decline lifecycle on a `care-team-invite` Task
// (see the participation-status note in the header: derived-from-Task is the
// honest shape; CareTeam itself is never written outside the Bot).
// ══════════════════════════════════════════════════════════════════════════════

export const CARETEAM_INVITE_TASK_TYPE = 'care-team-invite';
const TASK_TYPE_SYSTEM = 'https://healther.io/opd/task-type';
const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';

/** Map one raw invite Task to its lifecycle state. PURE.
 *  open → 'proposed'; completed → businessStatus 'declined' → 'declined', else
 *  'accepted'; cancelled → null (ignore — a withdrawn invite). */
export function inviteStateOfTask(task) {
  const s = (task && task.status) || '';
  if (s === 'cancelled') return null;
  if (s === 'completed') {
    const b = String((task.businessStatus && task.businessStatus.text) || '').toLowerCase();
    return b.includes('decline') ? 'declined' : 'accepted';
  }
  return 'proposed';
}

/**
 * Create the invite Task for a just-added member — ASSIGNED TO THE TARGET
 * (owner = the invited Practitioner) so it lands on THEIR Tasks page under
 * `Task?owner=%profile`. The Task inherits the patient's department compartment
 * (Task.for), which is what admits the inviter's write. Mirrors
 * medplum-tasks.js createTaskInMedplum's wire shape (task-type coding + the
 * opd-healther extension) so the Tasks screen's shaper reads it natively.
 */
export async function createCareTeamInvite({ patientId, patientDisplay, practitionerId, practitionerDisplay, role, careTeamId } = {}) {
  if (!patientId || !practitionerId) { const e = new Error('patient and practitioner are required'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const me = medplum.getProfile();
  const meName = (me && me.name && me.name[0] && (me.name[0].text
    || [(me.name[0].given || []).join(' '), me.name[0].family].filter(Boolean).join(' '))) || '';
  const roleLabel = seniorityLabelOf(role) || (role ? String(role) : '');
  const description = `You've been added to ${patientDisplay || 'a patient'}'s care team`
    + (roleLabel ? ` as ${roleLabel}` : '')
    + (meName ? ` by ${meName}` : '')
    + ' — accept or decline.';
  const task = {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    priority: 'routine',
    code: { text: CARETEAM_INVITE_TASK_TYPE, coding: [{ system: TASK_TYPE_SYSTEM, code: CARETEAM_INVITE_TASK_TYPE, display: 'Care-team invite' }] },
    description,
    authoredOn: new Date().toISOString(),
    for: { reference: `Patient/${patientId}`, display: patientDisplay || undefined },
    owner: { reference: `Practitioner/${practitionerId}`, display: practitionerDisplay || undefined },
    ...(careTeamId ? { focus: { reference: `CareTeam/${careTeamId}` } } : {}),
    ...(meName ? { requester: { display: meName } } : {}),
    extension: [{
      url: EXT_OPD_HEALTHER,
      valueString: JSON.stringify({
        kind: 'opd-task', taskType: CARETEAM_INVITE_TASK_TYPE,
        ownerId: practitionerId, performerRole: null, dept: null,
        createdBy: (me && me.id) || null,
      }),
    }],
  };
  return medplum.createResource(task);
}

/**
 * The invite state per member for one patient's team → { 'Practitioner/<id>':
 * { state:'proposed'|'accepted'|'declined', taskId } }. NEWEST invite per member
 * wins (a re-invite supersedes a decline). Best-effort: RLS may hide another
 * department's invite Task from this reader → that member simply shows without a
 * pending badge; a search failure returns {}.
 */
export async function listCareTeamInvites(patientId) {
  if (!patientId) return {};
  const { medplum } = await getMedplum();
  let tasks = [];
  try {
    tasks = await medplum.searchResources('Task',
      `code=${encodeURIComponent(`${TASK_TYPE_SYSTEM}|${CARETEAM_INVITE_TASK_TYPE}`)}&patient=${encodeURIComponent(`Patient/${patientId}`)}&_sort=-_lastUpdated&_count=100`);
  } catch (_) { return {}; }
  const out = {};
  for (const t of (tasks || [])) {
    const ref = (t.owner && t.owner.reference) || '';
    if (!/^Practitioner\//.test(ref) || out[ref]) continue; // newest-first sort → first wins
    const state = inviteStateOfTask(t);
    if (state) out[ref] = { state, taskId: t.id };
  }
  return out;
}

// ══════════════════════════════════════════════════════════════════════════════
// WRITE — CarePlan (direct, honest RLS write as the logged-in doctor).
// ══════════════════════════════════════════════════════════════════════════════

async function readPlan(medplum, planId) {
  const cp = await medplum.readResource('CarePlan', planId);
  if (!cp) { const e = new Error('care plan not found'); e.status = 404; throw e; }
  return cp;
}

/**
 * ADD a plan item (activity) to an existing CarePlan — the "change the plan"
 * affordance. Direct updateResource, RLS-bounded. `status` defaults to 'scheduled'
 * (an upcoming step). Returns the reshaped plan. Fail loud on a forbidden write.
 */
export async function addPlanActivity(planId, { description, scheduled, status = 'scheduled', category } = {}) {
  const desc = String(description || '').trim();
  if (!desc) { const e = new Error('a description is required'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const cp = await readPlan(medplum, planId);
  const detail = { status, description: desc };
  if (scheduled) detail.scheduledString = String(scheduled);
  if (category) detail.category = { text: String(category) };
  const next = { ...cp, activity: [...(Array.isArray(cp.activity) ? cp.activity : []), { detail }] };
  const saved = await medplum.updateResource(next);
  return shapeCarePlan(saved);
}

/**
 * Advance / set the status of one activity by index (mark a step done, start it,
 * put it on hold, …). Direct updateResource. Returns the reshaped plan.
 */
export async function setActivityStatus(planId, index, status) {
  const { medplum } = await getMedplum();
  const cp = await readPlan(medplum, planId);
  const acts = Array.isArray(cp.activity) ? cp.activity.slice() : [];
  if (index < 0 || index >= acts.length) { const e = new Error('no such plan step'); e.status = 400; throw e; }
  const a = acts[index] || {};
  acts[index] = { ...a, detail: { ...(a.detail || {}), status } };
  const saved = await medplum.updateResource({ ...cp, activity: acts });
  return shapeCarePlan(saved);
}

/**
 * Set the whole plan's lifecycle status (active | on-hold | completed | revoked …).
 * Direct updateResource. Returns the reshaped plan.
 */
export async function setPlanStatus(planId, status) {
  const { medplum } = await getMedplum();
  const cp = await readPlan(medplum, planId);
  const saved = await medplum.updateResource({ ...cp, status });
  return shapeCarePlan(saved);
}

/**
 * Create a per-patient CarePlan when the patient has none yet, then the caller can
 * add activities. Attempts a direct create (RLS: succeeds when the patient is in the
 * caller's compartment, which a bed patient in the caller's ward is). Fail loud
 * otherwise so the UI can explain rather than pretend.
 */
export async function createPatientCarePlan(patientId, patientDisplay, { title } = {}) {
  if (!patientId) { const e = new Error('no patient'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const body = {
    resourceType: 'CarePlan', status: 'active', intent: 'plan',
    title: title || (patientDisplay ? `${patientDisplay} — care plan` : 'Care plan'),
    identifier: [{ system: CAREPLAN_PATIENT_SYSTEM, value: String(patientId) }],
    subject: { reference: `Patient/${patientId}`, display: patientDisplay || undefined },
    period: { start: new Date().toISOString() },
  };
  const saved = await medplum.createResource(body);
  return shapeCarePlan(saved);
}

// ══════════════════════════════════════════════════════════════════════════════
// WRITE — CareTeam membership via the PRIVILEGED care-team-operation Bot.
// The browser calls Patient/$set-care-team AS THE LOGGED-IN USER; the Bot (runAsUser
// =false) enforces the seniority-hierarchy guard off the server-set caller identity.
// A refusal comes back as a 400 whose OperationOutcome carries the honest message —
// we re-throw it with .refused so the UI can show it verbatim (fail loud, §1.3).
// ══════════════════════════════════════════════════════════════════════════════

// Parse the Bot's success Parameters ({ careTeam, participantCount, outcome, … }) into
// a small result DTO. On the live custom-op path the return is a FHIR Parameters.
function parseCareTeamParams(out) {
  if (out && out.resourceType === 'Parameters' && Array.isArray(out.parameter)) {
    const o = {};
    for (const p of out.parameter) {
      if (!p || !p.name) continue;
      o[p.name] = p.valueString ?? p.valueInteger ?? p.valueBoolean ?? p.valueReference?.reference;
    }
    return o;
  }
  return out || {};
}

/**
 * Add or remove a practitioner on the patient's care team via the Bot.
 * `role` is the participant's role on the team (a seniority code from
 * SENIORITY_ROLE_OPTIONS, or free text). `op` is 'add' (default) or 'remove'.
 * Returns { careTeam, participantCount, outcome, … } on success. On a guard refusal
 * throws an Error with .refused=true and the Bot's honest message.
 *
 * INVITE (2026-07-10): a successful NEW add ALSO creates the `care-team-invite`
 * Task assigned to the target (accept/decline on their Tasks page — see the
 * participation-status note in the header). The member is 'proposed' until they
 * accept. The invite is created AFTER the Bot add succeeds; if the Task write
 * fails the add still stands and the result carries `inviteError` so the UI
 * reports the partial outcome honestly instead of pretending.
 */
export async function addCareTeamMember(patientId, { practitionerId, practitionerDisplay, patientDisplay, role, op = 'add' } = {}) {
  if (!patientId) { const e = new Error('no patient'); e.status = 400; throw e; }
  if (!practitionerId) { const e = new Error('pick a staff member'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const url = medplum.fhirUrl('Patient', '$set-care-team');
  const body = {
    patient: `Patient/${patientId}`,
    practitioner: `Practitioner/${practitionerId}`,
    ...(role ? { role: String(role) } : {}),
    op: op === 'remove' ? 'remove' : 'add',
  };
  let result;
  try {
    const out = await medplum.post(url, body);
    result = parseCareTeamParams(out);
  } catch (e) {
    // A refusal (seniority guard) / any Bot error → a 400 whose OperationOutcome
    // carries the message. Surface it verbatim, flagged refused for the UI.
    const oc = e && e.outcome;
    const msg = (oc && oc.issue && oc.issue[0] && (oc.issue[0].details?.text || oc.issue[0].diagnostics))
      || (e && e.message) || 'The care-team change was not permitted.';
    const err = new Error(msg);
    err.refused = true;
    throw err;
  }
  // A NEW membership (not a no-op / role tweak / removal) → invite the target.
  if (body.op === 'add' && result && result.outcome === 'added') {
    try {
      const invite = await createCareTeamInvite({
        patientId, patientDisplay, practitionerId, practitionerDisplay, role,
        careTeamId: idOf(result.careTeam),
      });
      result.inviteTaskId = invite.id;
    } catch (e) {
      result.inviteError = (e && e.message) || 'The invite task could not be created.';
    }
  }
  return result;
}

/**
 * Candidate staff for the add-member picker — every Practitioner the caller can read
 * (Practitioner is readable under every staff policy). Reused from the Task assignee
 * roster shape: [{ id, name }]. The Bot enforces WHO may actually be added, so the
 * picker does not pre-filter (owner spec: pick → confirm → honest refusal if too junior).
 */
export async function loadCareTeamCandidates() {
  const { medplum } = await getMedplum();
  const raw = await medplum.searchResources('Practitioner', '_count=200&_sort=name');
  const list = Array.isArray(raw) ? Array.from(raw) : [];
  return list.map((p) => {
    const n = (p.name && p.name[0]) || null;
    const name = (n && (n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' '))) || 'Practitioner';
    return { id: p.id, name };
  }).filter((s) => s.id);
}

// ══════════════════════════════════════════════════════════════════════════════
// CARE-PLAN TEMPLATES + CREATION — template pick OR spoken/typed extraction, both
// landing in the SAME editable draft { title, goals[], steps[] } the tab renders,
// saved via the proven direct CarePlan write path.
//
// TEMPLATE HOME (decision, 2026-07-10): FHIR PlanDefinition IS the canonical
// template resource, and the repo ALREADY has that machinery — the care-template
// protocols (scripts/medplum/seed-care-templates.mjs seeds department-owned
// PlanDefinitions; opd-app/src/lib/medplum-protocols.js reads them browser-direct;
// the manage-protocol Bot writes them). We REUSE that shape verbatim: the picker
// is listProtocolsForDepartment (own dept + hospital-wide, PlanDefinition readonly
// on every doctor policy) and a picked template's action[] titles become the draft
// steps, PlanDefinition.goal[].description the draft goals, with lineage kept on
// CarePlan.instantiatesCanonical — exactly what server/opd/care-templates.mjs
// $apply records. No parallel template store is invented.
// ══════════════════════════════════════════════════════════════════════════════

/** The template picker's list — the caller's own department's protocols +
 *  hospital-wide ones (the existing medplum-protocols read). */
export async function listCarePlanTemplates() {
  const me = await deriveCurrentUser();
  return listProtocolsForDepartment(me.department || null);
}

/** PlanDefinition → the editable draft shape. PURE.
 *  goals from PlanDefinition.goal[].description (CodeableConcept), steps from
 *  action[].title (+ timingString when authored), lineage from `url`. */
export function templateDraft(pd) {
  if (!pd) return null;
  const goals = (Array.isArray(pd.goal) ? pd.goal : [])
    .map((g) => (g.description && (g.description.text || g.description.coding?.[0]?.display)) || '')
    .map((s) => s.trim()).filter(Boolean);
  const steps = (Array.isArray(pd.action) ? pd.action : [])
    .filter((a) => a && a.title)
    .map((a) => ({ description: String(a.title).trim(), when: a.timingString ? String(a.timingString) : '' }));
  return {
    title: pd.title || pd.name || 'Care plan',
    goals,
    steps,
    instantiatesCanonical: pd.url || null,
  };
}

/** Read the raw PlanDefinition behind a picker row and shape it into a draft. */
export async function loadTemplateDraft(templateId) {
  const { medplum } = await getMedplum();
  const pd = await medplum.readResource('PlanDefinition', templateId);
  return templateDraft(pd);
}

// ── Voice/typed EXTRACTION → draft, via native `$ai` AS THE LOGGED-IN USER ─────
// The ipd-assess-agent pattern verbatim: ONE closed-schema tool bounds where facts
// can land; the model proposes, the validator drops anything illegal (never
// coerces), the editable draft is the review surface, and NOTHING is written here.
// FAIL LOUD on any `$ai` failure — no offline fallback (the typed template path
// still works end-to-end without AI).
const CARE_PLAN_EXTRACT_MODEL = 'gpt-5.4-mini';

export function buildCarePlanExtractTool() {
  return {
    type: 'function',
    function: {
      name: 'emit_care_plan',
      description: 'Emit the care plan extracted from the clinician\'s dictation. Call exactly once.',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          title: { type: 'string', description: 'Short plan title in the clinician\'s own words (e.g. "Post-op knee replacement").' },
          goals: {
            type: 'array', items: { type: 'string' },
            description: 'Each goal EXACTLY as stated (e.g. "pain below 4"). Empty if none were stated.',
          },
          steps: {
            type: 'array',
            items: {
              type: 'object', additionalProperties: false,
              properties: {
                description: { type: 'string', description: 'The step, as stated.' },
                when: { type: 'string', description: 'The stated timing verbatim (e.g. "day 1", "day 3"). Empty if none.' },
              },
              required: ['description'],
            },
            description: 'The plan steps in the order stated.',
          },
        },
        required: ['title', 'steps'],
      },
    },
  };
}

export function buildCarePlanExtractPrompt() {
  return [
    'You extract a structured care plan from a clinician\'s dictation for an admitted patient.',
    'Rules:',
    '- Use ONLY what the clinician said. NEVER invent steps, goals, timings, medications, or severities that were not stated.',
    '- Keep the clinician\'s wording; do not paraphrase clinical content.',
    '- Timings ("day 1", "week 2", "after physio review") go in each step\'s `when`, verbatim.',
    '- Goals are targets/outcomes ("pain below 4", "independent ambulation"); steps are actions.',
    '- Call emit_care_plan exactly once with everything extracted, then reply with one short confirmation sentence.',
  ].join('\n');
}

/** Validate/normalise a raw emit_care_plan payload into the draft shape. PURE.
 *  Illegal entries are DROPPED (never coerced) and reported in `warnings`. */
export function validateCarePlanDraft(raw) {
  const warnings = [];
  const src = (raw && typeof raw === 'object') ? raw : {};
  const title = String(src.title || '').trim();
  if (!title) warnings.push('no title extracted');
  const goals = [];
  for (const g of (Array.isArray(src.goals) ? src.goals : [])) {
    const s = String(g || '').trim();
    if (s) goals.push(s);
    else warnings.push('dropped an empty goal');
  }
  const steps = [];
  for (const st of (Array.isArray(src.steps) ? src.steps : [])) {
    const d = String((st && st.description) || '').trim();
    if (!d) { warnings.push('dropped a step with no description'); continue; }
    steps.push({ description: d, when: String((st && st.when) || '').trim() });
  }
  if (!steps.length) warnings.push('no plan steps extracted');
  return { draft: { title: title || 'Care plan', goals, steps, instantiatesCanonical: null }, warnings };
}

/**
 * extractCarePlanDraft(utterance) → { draft, warnings, model }.
 * Runs `$ai` as the logged-in user; THROWS on failure (fail loud) — the caller
 * shows the honest error and the typed/template path remains available.
 */
export async function extractCarePlanDraft(utterance) {
  const u = String(utterance || '').trim();
  if (!u) { const e = new Error('say or type the plan first'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const collected = { raw: null };
  await runAgentLoop({
    medplum,
    model: CARE_PLAN_EXTRACT_MODEL,
    systemPrompt: buildCarePlanExtractPrompt(),
    messages: [{ role: 'user', content: u }],
    tools: [buildCarePlanExtractTool()],
    executeToolCall: async (name, args) => {
      if (name !== 'emit_care_plan') {
        return { toolResult: { error: `unknown tool ${name}` }, step: { tool: name, ok: false } };
      }
      collected.raw = args;
      return {
        toolResult: { ok: true, note: 'Plan captured. Do not call any more tools; reply with one short confirmation.' },
        step: { tool: 'emit_care_plan', ok: true },
      };
    },
    maxIterations: 3,
  });
  if (!collected.raw) { const e = new Error('the model returned no plan (emit_care_plan was never called)'); throw e; }
  const { draft, warnings } = validateCarePlanDraft(collected.raw);
  return { draft, warnings, model: CARE_PLAN_EXTRACT_MODEL };
}

/**
 * Save an edited draft as the patient's CarePlan — the SAME direct write path as
 * createPatientCarePlan, with goals (goal[].display — the honest home given Goal
 * resources are read-forbidden here), activities (status 'scheduled'), and
 * template lineage (instantiatesCanonical). Saved status 'draft' — the plan is
 * real but explicitly not yet activated; the tab offers Activate (setPlanStatus).
 */
export async function createCarePlanFromDraft(patientId, patientDisplay, draft) {
  if (!patientId) { const e = new Error('no patient'); e.status = 400; throw e; }
  const d = draft || {};
  const steps = (Array.isArray(d.steps) ? d.steps : []).filter((s) => s && String(s.description || '').trim());
  if (!steps.length) { const e = new Error('the plan needs at least one step'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const body = {
    resourceType: 'CarePlan', status: 'draft', intent: 'plan',
    title: String(d.title || '').trim() || (patientDisplay ? `${patientDisplay} — care plan` : 'Care plan'),
    identifier: [{ system: CAREPLAN_PATIENT_SYSTEM, value: String(patientId) }],
    subject: { reference: `Patient/${patientId}`, display: patientDisplay || undefined },
    period: { start: new Date().toISOString() },
    ...(d.instantiatesCanonical ? { instantiatesCanonical: [String(d.instantiatesCanonical)] } : {}),
    ...((Array.isArray(d.goals) && d.goals.length)
      ? { goal: d.goals.map((g) => ({ display: String(g).trim() })).filter((g) => g.display) }
      : {}),
    activity: steps.map((s) => ({
      detail: {
        status: 'scheduled',
        description: String(s.description).trim(),
        ...(String(s.when || '').trim() ? { scheduledString: String(s.when).trim() } : {}),
      },
    })),
  };
  const saved = await medplum.createResource(body);
  return shapeCarePlan(saved);
}

// ══════════════════════════════════════════════════════════════════════════════
// DEFAULT CARE TEAM (admin config) — a definitional (template) CareTeam per
// department: NO subject (it is not about one patient), status 'proposed',
// managingOrganization = the department Organization, participant[] = the default
// roles. Stored FHIR-correctly so the ward can later instantiate a patient team
// from it. Writes run as the ADMIN (wildcard opd-admin policy) — verified writable.
// ══════════════════════════════════════════════════════════════════════════════

/** Resolve the department Organization reference for a slug (Organization/uuid), or
 *  a logical identifier ref if it hasn't synced — best-effort. */
async function resolveDeptOrgRef(medplum, deptSlug, deptName) {
  try {
    const orgs = await medplum.searchResources('Organization',
      `identifier=${encodeURIComponent(DEPT_SLUG_SYSTEM + '|' + deptSlug)}&_count=1`);
    if (orgs && orgs[0]) return { reference: `Organization/${orgs[0].id}`, display: orgs[0].name || deptName };
  } catch (_) { /* fall through to a logical ref */ }
  return { identifier: { system: DEPT_SLUG_SYSTEM, value: String(deptSlug) }, display: deptName || String(deptSlug) };
}

/** Shape a definitional CareTeam into the admin editor's row model. PURE. */
export function shapeDefaultTeam(ct) {
  if (!ct) return { id: null, roles: [] };
  return {
    id: ct.id,
    roles: (Array.isArray(ct.participant) ? ct.participant : []).map((p) => ({
      role: (Array.isArray(p.role) ? p.role : []).map((r) => r.text || r.coding?.[0]?.display).filter(Boolean).join(' · ') || '',
      member: refDisplay(p.member) || '',
    })),
  };
}

/** Load the default (template) team for one department slug → { id, roles }. */
export async function loadDefaultCareTeam(deptSlug) {
  const { medplum } = await getMedplum();
  const found = await medplum.searchResources('CareTeam',
    `identifier=${encodeURIComponent(CARETEAM_DEFAULT_SYSTEM + '|' + deptSlug)}&_count=1`).catch(() => []);
  return shapeDefaultTeam(found && found[0]);
}

/** Load default teams for many department slugs at once → { [slug]: {id,roles} }. */
export async function loadDefaultCareTeams(deptSlugs) {
  const out = {};
  await Promise.all((deptSlugs || []).map(async (slug) => { out[slug] = await loadDefaultCareTeam(slug).catch(() => ({ id: null, roles: [] })); }));
  return out;
}

/**
 * Save (upsert) the default team for a department. `roles` = [{ role, member? }].
 * Empty roles are dropped. Idempotent by the department slug identifier. Direct
 * write as the admin. Returns the reshaped { id, roles }.
 */
export async function saveDefaultCareTeam(deptSlug, deptName, roles) {
  if (!deptSlug) { const e = new Error('no department'); e.status = 400; throw e; }
  const { medplum } = await getMedplum();
  const orgRef = await resolveDeptOrgRef(medplum, deptSlug, deptName);
  const participant = (Array.isArray(roles) ? roles : [])
    .map((r) => ({ role: String(r.role || '').trim(), member: String(r.member || '').trim() }))
    .filter((r) => r.role || r.member)
    .map((r) => ({
      ...(r.role ? { role: [{ text: r.role }] } : {}),
      ...(r.member ? { member: { display: r.member } } : {}),
    }));
  const body = {
    resourceType: 'CareTeam',
    status: 'proposed', // definitional/template — not an active patient team
    name: `${deptName || deptSlug} — default care team`,
    identifier: [{ system: CARETEAM_DEFAULT_SYSTEM, value: String(deptSlug) }],
    managingOrganization: [orgRef],
    participant,
  };
  const existing = (await medplum.searchResources('CareTeam',
    `identifier=${encodeURIComponent(CARETEAM_DEFAULT_SYSTEM + '|' + deptSlug)}&_count=1`).catch(() => []))[0];
  const saved = existing
    ? await medplum.updateResource({ ...body, id: existing.id })
    : await medplum.createResource(body);
  return shapeDefaultTeam(saved);
}

// Test surface (pure helpers) for offline verification.
export const __careTest = {
  activityBand, shapeActivity, shapeCarePlan, carePlanProgress, shapeCareTeam, shapeDefaultTeam,
  buildCareTeamTree, inviteStateOfTask, seniorityRankOf, seniorityLabelOf,
  templateDraft, validateCarePlanDraft, buildCarePlanExtractTool, buildCarePlanExtractPrompt,
};
